<template>
  <div>
    <div v-if="loading" style="text-align: center; padding-top: 50px;">
      <b-spinner style="height: 40px; width: 40px;" variant="primary"></b-spinner>
    </div>

    <b-alert :show="!loading && fatalError" variant="danger" style="text-align: center;">
      {{ $t('dashboard:edit_page.fatal_error', 'An error occurred. Please try reloading the page.') }}
    </b-alert>

    <div v-if="!loading && !fatalError">

      <splitter-context-helper
        v-if="panel"
        :label="$t('dashboard:panel_data_settings.context.panel', 'Panel')"
        :value="panel.title"
      />

      <div
        v-if="panel"
        class="panel-data-settings"
        style="padding: 15px 9px;"
        data-testid="panelDataSettings"
      >
        <div style="text-align: right; margin-bottom: 10px;">
          <b-button
            v-if="sources.length"
            class="btn-ghost-primary"
            @click="expandAll"
            size="sm"
          >
            {{ $t('dashboard:panel_data_settings.expand_all_sources', 'Expand All') }}
          </b-button>
          <b-button
            style="margin-left: 10px;"
            v-if="sources.length"
            class="btn-ghost-primary"
            @click="collapseAll"
            size="sm"
          >
            {{ $t('dashboard:panel_data_settings.collapse_all_sources', 'Collapse All') }}
          </b-button>
        </div>

        <cc-expandable-select
          v-for="(singleSource, sourceIndex) of sources"
          :title="singleSource.displayName"
          :key="singleSource.key"
          :expanded.sync="singleSource.expanded"
          :data-testid="'expSelSource' + sourceIndex"
        >
          <div slot="toggle-switch">
            <div v-if="!stationVariableActive && singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id"
              v-b-tooltip.hover.left
              :title="$t('dashboard:panel_data_settings.station_variable_warning', 'This series will not be used unless the station variable is activated.')"
              class="source-warning-indicator"
            >
              <i class="fa fa-exclamation"></i>
            </div>
            <b-dropdown
              v-if="!formDisabled"
              variant="link"
              class="m-md-2"
              no-caret
            >
              <template #button-content>
                <i class="fa fa-ellipsis-h"></i>
              </template>
              <b-dropdown-item
                @click="deleteSource(sourceIndex)"
                data-testid="mniSourceDelete"
              >
                {{ $t('common:single_word.delete', 'Delete') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>


          <div style="padding: 15px;">
            <cc-form-input label="Configure as variable" label-cols="auto">
              <div style="text-align: right;">
                <b-form-checkbox
                  switch
                  :checked="singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id"
                  @change="toggleVariable(singleSource)"
                  data-testid="cboSourceVariable"
                ></b-form-checkbox>
              </div>
            </cc-form-input>

            <div v-if="singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id">
              <div style="display: flex; flex-direction: row; margin-bottom: 10px;">
                <div style="margin-right: 5px; line-height: 34px; flex: 0 0 35px;">
                  {{$t('common:single_word.filter', 'Filter')}}
                </div>
                <b-form-select
                  v-model="singleSource.filters[0].type"
                  :options="filterTypes"
                  style="margin-right: 5px; flex: 3;"
                  @change="singleSource.filters[0].value = null"
                  :disabled="formDisabled"
                  data-testid="ddlSourceFilterType"
                ></b-form-select>
                <b-form-select
                  v-model="singleSource.filters[0].operator"
                  :options="filterOperators"
                  style="margin-right: 5px; flex: 1; max-width: 70px;"
                  :disabled="formDisabled"
                  data-testid="ddlSourceFilterOperator"
                ></b-form-select>
                <b-form-select
                  v-model="singleSource.filters[0].value"
                  :options="filterValues(singleSource.filters[0].type)"
                  style="flex: 3 1 0%; margin-right: 5px;"
                  :disabled="formDisabled"
                  data-testid="ddlSourceFilterValue"
                ></b-form-select>
                <!-- For styling consistency -->
                <b-button
                  v-if="!formDisabled && singleSource.filters.length > 1"
                  style="visibility: hidden;"
                    variant="danger"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>

              <div
                v-for="(filter, filterIndex) of singleSource.filters"
                :key="filter.key"
                style="margin-bottom: 10px;"
              >
                <div v-if="filterIndex !== 0" style="display: flex; flex-direction: row;">
                  <div style="margin-right: 5px; line-height: 34px; flex: 0 0 35px;">
                    {{$t('common:single_word.and', 'and')}}
                  </div>
                  <b-form-select
                    :options="filterTypes"
                    v-model="singleSource.filters[filterIndex].type"
                    style="margin-right: 5px; flex: 3;"
                    @change="singleSource.filters[filterIndex].value = null"
                    :disabled="formDisabled"
                    :data-testid="'ddlSourceFilterType' + filterIndex"
                  ></b-form-select>
                  <b-form-select
                    :options="filterOperators"
                    v-model="singleSource.filters[filterIndex].operator"
                    style="margin-right: 5px; flex: 1; max-width: 70px;"
                    :disabled="formDisabled"
                    :data-testid="'ddlSourceFilterOperator' + filterIndex"
                  ></b-form-select>
                  <b-form-select 
                    :options="filterValues(singleSource.filters[filterIndex].type)"
                    v-model="singleSource.filters[filterIndex].value"
                    style="flex: 3; margin-right: 5px;"
                    :disabled="formDisabled"
                    :data-testid="'ddlSourceFilterValue' + filterIndex"
                  ></b-form-select>
                  <b-button
                    v-if="!formDisabled"
                    variant="danger"
                    @click="deleteFilter(sourceIndex, filterIndex)"
                  >
                    <i class="fa fa-times"></i>
                  </b-button>
                </div>
              </div>

              <div style="display: flex; flex-direction: row; margin-bottom: 10px;">
                <div style="flex: 1"></div>
                <b-button v-if="!formDisabled" variant="success" @click="addFilter(sourceIndex)"
                  data-testid="btnAddSourceFilter"
                ><i class="fa fa-plus"></i></b-button>
              </div>
            </div>

            <div v-if="singleSource.type === DATA_SOURCE_TYPE.exactMeasurement.id" class="exact-measurement-source-container">
              <div class="labels">
                <legend class="col-auto bv-no-focus-ring col-form-label">
                  <div>
                    {{$t('common.single_word.station', 'Station')}}
                  </div>
                </legend>
                <legend class="col-auto bv-no-focus-ring col-form-label">
                  <div>
                    {{$t('common.single_word.measurement', 'Measurement')}}
                  </div>
                </legend>
              </div>
              <div class="selectors">
                <b-form-select
                  :options="stationDropdownOptions"
                  v-model="singleSource.station"
                  @change="stationChanged(sourceIndex)"
                  :disabled="formDisabled"
                  data-testid="ddlSourceStation"
                ></b-form-select>
                <b-form-select
                  :options="stationMeasurementOptions(singleSource.station)"
                  v-model="singleSource.measurement"
                  :disabled="formDisabled"
                  data-testid="ddlSourceMeasurement"
                ></b-form-select>
              </div>
            </div>
          </div>
        </cc-expandable-select>

        <div style="text-align: center;">
          <b-button v-if="!formDisabled" variant="success" @click="addSource"
            data-testid="btnAddSource">
            <i class="fa fa-plus"></i> {{ $t('dashboard:panel_data_settings.add_source', 'Add Source') }}
          </b-button>
        </div>
      </div>

      <div v-if="!panel" class="no-panel-selected">
        {{ $t("dashboard:panel_data_settings.no_panel_selected", 'Select a panel to edit. (Click the panel title and choose "modify data")') }}
      </div>

    </div>
  </div>
</template>

<style>
.panel-data-settings .cc-expandable-select-line-1 {
  font-weight: bold;
}
.source-warning-indicator {
  width: 30px;
  height: 30px;
  line-height: 30px;
  display: inline-block;
  text-align: center;
  color: #eb0000;
  font-size: 1.2rem;
  border-radius: 3px;
}
.source-warning-indicator:hover {
  background-color: #f2f2f2;
}

.exact-measurement-source-container .labels, .exact-measurement-source-container .selectors {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}
.exact-measurement-source-container .selectors {
  flex-grow: 4;
}
.exact-measurement-source-container .selectors select {
  margin: .5em 0;
}
.exact-measurement-source-container .selectors select:first-child {
  margin-top: 0;
}
.exact-measurement-source-container.selectors select:last-child {
  margin-bottom: 0;
}
.exact-measurement-source-container {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}
</style>

<script>
import SplitterContextHelper from '@/components/SplitterContextHelper';
import DATA_SOURCE_TYPE from '@/shared/data-source-types';

export default {
  name: 'panel-data-settings',

  components: {
    SplitterContextHelper,
  },

  props: {
    saveTrigger: {
      type: Boolean,
    },
    panel: {
      type: Object,
    },
    stationVariableActive: {
      type: Boolean,
      required: true,
    },
    uom: {
      type: Object,
    },
  },

  data() {
    return {
      sources: [],
      indexKey: 1,
      formDisabled: false,
      DATA_SOURCE_TYPE: DATA_SOURCE_TYPE,
    };
  },

  computed: {
    loading() {
      return this.$store.state.stationsLoading;
    },
    fatalError() {
      return this.$store.state.stationsError;
    },
    stations() {
      return this.$store.state.stations;
    },
    stationDropdownOptions() {
      if (this.stations) {
        return this.stations.map(e => {
          return { text: e.name, value: e.id };
        }).sort((a, b) => a.text.localeCompare(b.text));
      } else {
        return [];
      }
    },
    filterTypes() {
      return [
        {
          text: this.$t('dashboard:panel_data_settings.filter_type.measurement_type','Measurement Type'),
          value: 'measurement_type',
        },
        {
          text: this.$t('common:phrase.unit_of_measure','Unit of Measure'),
          value: 'uom',
        },
      ];
    },
    filterOperators() {
      return [
        {
          text: '=',
          value: '=',
        },
        {
          text: '!=',
          value: '!=',
        },
      ];
    },
    uomFilterValues() {
      if (!this.stations) {
        return [];
      } else {
        const values = {};

        for (const station of this.stations) {
          for (const measurementDefinition of station.sensor_definitions) {
            if (measurementDefinition.unit_id) {
              values[measurementDefinition.unit_id] = true;
            }
          }
        }

        // also add in any units that were previously selected, but
        // no longer appear on any measurement definitions
        if (this.panel && this.panel.data && this.panel.data.sources) {
          for (const singleSource of this.panel.data.sources) {
            if (singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id) {
              for (const filter of singleSource.options.filters) {
                if (filter.type === 'uom' && filter.value) {
                  values[filter.value] = true;
                }
              }
            }
          }
        }

        // no stations have units, just use all units
        if (Object.keys(values).length === 0) {
          return this.uom.units.map(e => {
            return {
              text: e.symbol,
              value: e.unit_id,
            };
          });
        } else {
          return Object.keys(values).map(e => {
          const match = this.uom.units.find(unit => unit.unit_id === e);

          return {
            text: match ? match.symbol : 'UNKNOWN UNIT',
            value: e,
          };
        }).sort((a, b) => a.text.localeCompare(b.text));
        }
      }
    },
    measurementTypeFilterValues() {
      if (!this.stations) {
        return [];
      } else {
        const values = {};

        for (const station of this.stations) {
          for (const measurementDefinition of station.sensor_definitions) {
            if (measurementDefinition.subtype_id) {
              values[measurementDefinition.subtype_id] = true;
            }
          }
        }

        // also add in any types that were previously selected, but
        // no longer appear on any measurement definitions
        if (this.panel && this.panel.data && this.panel.data.sources) {
          for (const singleSource of this.panel.data.sources) {
            if (singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id) {
              for (const filter of singleSource.options.filters) {
                if (filter.type === 'measurement_type' && filter.value) {
                  values[filter.value] = true;
                }
              }
            }
          }
        }

        // no stations have units, just use all subtypes
        if (Object.keys(values).length === 0) {
          return this.uom.subtypes.map(e => {
            return {
              text: this.$t(e.translation_key, e.description),
              value: e.subtype_id,
            };
          });
        } else {
          return Object.keys(values).map(e => {
            const match = this.uom.subtypes.find(subtype => subtype.subtype_id === e);

            return {
              text: match ? this.$t(match.translation_key, match.description) : 'UNKNOWN TYPE',
              value: e,
            };
          }).sort((a, b) => a.text.localeCompare(b.text));
        }
      }
    },
  },

  methods: {
    expandAll() {
      for (const source of this.sources) {
        source.expanded = true;
      }
    },

    collapseAll() {
      for (const source of this.sources) {
        source.expanded = false;
      }
    },

    toggleVariable(source) {
      source.type = (source.type === DATA_SOURCE_TYPE.variableMeasurements.id ?
        DATA_SOURCE_TYPE.exactMeasurement.id : DATA_SOURCE_TYPE.variableMeasurements.id);

      this.updateSourceDisplayNames();
    },

    updateSourceDisplayNames() {
      let countVariable = 0;
      let countExact = 0;

      for (const source of this.sources) {
        if (source.type === DATA_SOURCE_TYPE.variableMeasurements.id) {
          countVariable++;
          source.displayName =
            `${this.$t(`dashboard:data_source_type.${DATA_SOURCE_TYPE.variableMeasurements.id}.title`, 'Variable Series')} ${countVariable}`;
        } else {
          countExact++;
          source.displayName =
            `${this.$t(`dashboard:data_source_type.${DATA_SOURCE_TYPE.exactMeasurement.id}.title`, 'Series')} ${countExact}`;
        }
      }
    },

    deleteSource(index) {
      this.sources.splice(index, 1);
      this.updateSourceDisplayNames();
    },

    getUniqueKey() {
      return this.indexKey++;
    },

    deleteFilter(sourceIndex, filterIndex) {
      this.sources[sourceIndex].filters.splice(filterIndex, 1);
    },

    addFilter(index) {
      this.sources[index].filters.push({
        type: null,
        operator: null,
        value: null,
        key: this.getUniqueKey(),
      });
    },

    filterValues(filterType) {
      if (filterType === 'uom') {
        return this.uomFilterValues;
      } else if (filterType === 'measurement_type') {
        return this.measurementTypeFilterValues;
      } else {
        return [];
      }
    },

    stationMeasurementOptions(stationId) {
      if (!stationId) {
        return [];
      } else {
        return this.stations.find(e => e.id === stationId).sensor_definitions.map(e => {
          return { text: e.alias, value: e.name };
        }).sort((a, b) => a.text.localeCompare(b.text));
      }
    },

    stationChanged(index) {
      this.sources[index].measurement = null;
    },

    addSource() {
      if (this.sources.length >= 20) {
        this.$Toastr = {
          type: "error",
          message: this.$t("dashboard:panel_data_settings.max_sources_reached", "You have already added the maximum number of data sources."),
        };
      } else {
        this.sources.push(this.blankSource());
        this.updateSourceDisplayNames();
      }
    },

    blankSource() {
      return {
        station: null,
        measurement: null,
        expanded: true,
        type: DATA_SOURCE_TYPE.exactMeasurement.id,
        filters: [
          {
            type: null,
            operator: null,
            value: null,
          },
        ],
        key: this.getUniqueKey(),
        displayName: '',
      };
    },

    init() {
      this.formDisabled = !this.$s.can.access('dashboard..entity.update');
      
      if (this.panel) {
        this.refreshForm(this.panel);
      }
    },

    refreshForm(panel) {
      const sources = [];

      if (panel.data && panel.data.sources) {
        for (const singleSource of panel.data.sources) {
          const formSourceObject = this.blankSource();
          formSourceObject.type = singleSource.type;

          if (singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id) {
            formSourceObject.filters = JSON.parse(JSON.stringify(singleSource.options.filters));
          } else {
            formSourceObject.station = singleSource.options.station;
            formSourceObject.measurement = singleSource.options.measurement;
          }

          sources.push(formSourceObject);
        }
      }

      this.sources = sources;

      this.updateSourceDisplayNames();
    },

    validate() {
      let valid = true;

      for (const singleSource of this.sources) {
        if (singleSource.type === DATA_SOURCE_TYPE.variableMeasurements.id) {
          for (const filter of singleSource.filters) {
            if (!filter.type || !filter.operator || !filter.value) {
              singleSource.expanded = true;
              return false;
            }
          }
        } else {
          if (!singleSource.station || !singleSource.measurement) {
            singleSource.expanded = true;
            return false;
          }
        }
      }

      return valid;
    },

    // format the form data into the appropriate
    // dashboard.attributes object
    formatForAttributes() {
      const settings = { sources: [] };

      for (let source of this.sources) {
        if (source.type === DATA_SOURCE_TYPE.variableMeasurements.id) {
          settings.sources.push({
            type: DATA_SOURCE_TYPE.variableMeasurements.id,
            options: {
              filters: JSON.parse(JSON.stringify(source.filters)),
            },
          });
        } else {
          settings.sources.push({
            type: DATA_SOURCE_TYPE.exactMeasurement.id,
            options: {
              station: source.station,
              measurement: source.measurement,
            },
          });
        }
      }

      return settings;
    },

    emitValue() {
      const valid = this.validate();

      if (valid) {
        this.$emit('settings-updated', this.formatForAttributes());
      } else {
        this.$Toastr = {
          type: "error",
          message: this.$t("dashboard:panel_data_settings.toastr.invalid_form", "Please fix all form errors before saving."),
        };
      }
    },
  },

  watch: {
    saveTrigger(newValue, oldValue) {
      if (newValue === true) {
        this.emitValue();
        this.$emit('update:save-trigger', false);
      }
    },
    panel(newVal) {
      if (newVal !== null) {
        this.refreshForm(newVal);
      } else {
        this.sources = [];
      }
    },
  },

  mounted() {
    this.init();
  },
};
</script>